import { z } from 'zod';

/** Debrid services Tea+ can play torrent streams through. An account uses one at a time. */
export const PROVIDER_IDS = [
  'realdebrid',
  'alldebrid',
  'premiumize',
  'torbox',
  'debridlink',
  'offcloud',
] as const;
export type ProviderId = (typeof PROVIDER_IDS)[number];
export const providerIdSchema = z.enum(PROVIDER_IDS);

export interface ProviderMeta {
  id: ProviderId;
  name: string;
  /** The short tag addons use in stream names, e.g. [RD+]. */
  tag: string;
  website: string;
  signupUrl: string;
  premiumUrl: string;
  /** Where the person finds the API key Tea+ asks for. */
  keyUrl: string;
  keyLabel: string;
  blurb: string;
  /** Signs in with a code on the provider's site instead of pasting a key. */
  deviceLogin: boolean;
}

export const PROVIDERS: Record<ProviderId, ProviderMeta> = {
  realdebrid: {
    id: 'realdebrid',
    name: 'Real-Debrid',
    tag: 'RD',
    website: 'https://real-debrid.com',
    signupUrl: 'https://real-debrid.com/signup',
    premiumUrl: 'https://real-debrid.com/premium',
    keyUrl: 'https://real-debrid.com/apitoken',
    keyLabel: 'API token',
    blurb: 'The most widely supported service, with a huge cache. Pay for days, no subscription.',
    deviceLogin: true,
  },
  alldebrid: {
    id: 'alldebrid',
    name: 'AllDebrid',
    tag: 'AD',
    website: 'https://alldebrid.com',
    signupUrl: 'https://alldebrid.com/register/',
    premiumUrl: 'https://alldebrid.com/offer/',
    keyUrl: 'https://alldebrid.com/apikeys/',
    keyLabel: 'API key',
    blurb: 'A popular alternative with a large cache and many supported hosts.',
    deviceLogin: false,
  },
  premiumize: {
    id: 'premiumize',
    name: 'Premiumize',
    tag: 'PM',
    website: 'https://www.premiumize.me',
    signupUrl: 'https://www.premiumize.me/register',
    premiumUrl: 'https://www.premiumize.me/premium',
    keyUrl: 'https://www.premiumize.me/account',
    keyLabel: 'API key',
    blurb: 'A premium cloud downloader with fast servers and a cloud drive included.',
    deviceLogin: false,
  },
  torbox: {
    id: 'torbox',
    name: 'TorBox',
    tag: 'TB',
    website: 'https://torbox.app',
    signupUrl: 'https://torbox.app/login',
    premiumUrl: 'https://torbox.app/pricing',
    keyUrl: 'https://torbox.app/settings',
    keyLabel: 'API key',
    blurb: 'A newer service with monthly plans, a free tier and generous limits.',
    deviceLogin: false,
  },
  debridlink: {
    id: 'debridlink',
    name: 'Debrid-Link',
    tag: 'DL',
    website: 'https://debrid-link.com',
    signupUrl: 'https://debrid-link.com',
    premiumUrl: 'https://debrid-link.com/premium',
    keyUrl: 'https://debrid-link.com/webapp/apikey',
    keyLabel: 'API key',
    blurb: 'A European service with a seedbox for your torrents and good value plans.',
    deviceLogin: false,
  },
  offcloud: {
    id: 'offcloud',
    name: 'Offcloud',
    tag: 'OC',
    website: 'https://offcloud.com',
    signupUrl: 'https://offcloud.com',
    premiumUrl: 'https://offcloud.com',
    keyUrl: 'https://offcloud.com/#/account',
    keyLabel: 'API key',
    blurb: 'A cloud downloader that saves files to its cloud and streams them back.',
    deviceLogin: false,
  },
};

export const providerName = (id: ProviderId | null | undefined) =>
  id ? PROVIDERS[id].name : 'a provider';

export const connectTokenSchema = z.object({
  token: z.string().trim().min(10, 'That key looks too short').max(200),
});

export type ProviderConnectionStatus =
  'disconnected' | 'connected' | 'expired' | 'invalid' | 'restricted' | 'error';

export interface ProviderAccountInfo {
  username: string;
  type: string;
  premiumUntil: string | null;
  premiumDaysLeft: number | null;
}

export interface ProviderStatus {
  /** The connected provider, or null when none is. */
  provider: ProviderId | null;
  status: ProviderConnectionStatus;
  method: 'oauth' | 'token' | null;
  account: ProviderAccountInfo | null;
  connectedAt: string | null;
  lastValidatedAt: string | null;
  message: string | null;
  personalTokenAllowed: boolean;
}

export interface DeviceCodeStart {
  flowId: string;
  userCode: string;
  verificationUrl: string;
  expiresIn: number;
  interval: number;
}

export interface DeviceCodePoll {
  state: 'pending' | 'connected' | 'expired' | 'error';
  status?: ProviderStatus;
  message?: string;
}
